import { useTranslation } from 'react-i18next';
import { useCenterStaff } from '@hooks/usePublicBooking';
import { PageLoading } from '@components/common/LoadingSpinner';
import { ErrorMessage } from '@components/common/ErrorMessage';
import { Button } from '@components/ui/button';
import type { Staff } from '@api/types';
import { cn } from '@lib/utils';

interface StaffStepProps {
  centerSlug: string;
  serviceId: string;
  onSelect: (staff: Staff | null) => void;
  onSkip: () => void;
  onBack: () => void;
}

export function StaffStep({ centerSlug, serviceId, onSelect, onSkip, onBack }: StaffStepProps) {
  const { t } = useTranslation();
  const { data: staffList, isLoading, isError, refetch } = useCenterStaff(centerSlug, serviceId);

  if (isLoading) return <PageLoading />;
  if (isError) return <ErrorMessage onRetry={() => void refetch()} />;

  const active = staffList?.filter((s) => s.active) ?? [];

  return (
    <section aria-label={t('widget.selectStaff')}>
      <h2 className="mb-4 text-lg font-semibold">{t('widget.selectStaff')}</h2>

      <Button
        variant="outline"
        className="mb-4 w-full justify-start"
        onClick={onSkip}
        data-testid="staff-any"
      >
        {t('widget.anyStaff')}
      </Button>

      <ul className="grid gap-3" role="list">
        {active.map((staff) => (
          <li key={staff.id}>
            <button
              type="button"
              className={cn(
                'w-full rounded-md border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-primary/5',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              onClick={() => { onSelect(staff); }}
              data-testid={`staff-option-${staff.id}`}
            >
              <div className="font-medium">{staff.name}</div>
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <Button type="button" variant="outline" onClick={onBack} data-testid="staff-back">
          {t('common.back')}
        </Button>
      </div>
    </section>
  );
}
